import React, { useEffect, useState } from "react";
import {
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";

import {
  Form,
  Button,
  Card,
  Alert,
  Spinner,
  Tabs,
  Tab,
  Badge,
  Row,
  Col,
  InputGroup,
  ProgressBar,
} from "react-bootstrap";

import api from "./Api";

export default function EditQuestion() {
  const { id } = useParams();

  const navigate = useNavigate();

  const location = useLocation();

  const isDemo =
    new URLSearchParams(location.search).get(
      "demo"
    ) === "true";

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [message, setMessage] =
    useState("");

  const [useJsonEditor, setUseJsonEditor] =
    useState(false);

  const [meta, setMeta] = useState({});

  const [questype, setQuestype] =
    useState("");

  const [description, setDescription] =
    useState("");

  const [questions, setQuestions] =
    useState([]);

  const [jsonText, setJsonText] =
    useState("");

  const [banks, setBanks] = useState([]);

  const [bankId, setBankId] =
    useState("");

  // ================= LOAD BANKS =================
  useEffect(() => {
    api
      .get("/questionbank/banks")
      .then((res) => {
        if (
          res.data.status === "success"
        ) {
          setBanks(
            res.data.data || []
          );
        }
      })
      .catch(console.error);
  }, []);

  // ================= LOAD QUESTION =================
  useEffect(() => {
   const fetchUrl =
  `/pustak-questionbank/view-question/${id}?is_demo=${isDemo ? 1 : 0}`;

    api
      .get(fetchUrl)
      .then((res) => {
        const d = res.data.data;

        setMeta(d);

        setQuestype(d.questype);

        setDescription(
          d.description || ""
        );

        setQuestions(d.quesJson || []);

        setJsonText(
          JSON.stringify(
            d.quesJson,
            null,
            2
          )
        );

        setBankId(
          d.quesbankid || ""
        );
      })
      .catch(() =>
        setMessage(
          "❌ Failed to load data"
        )
      )
      .finally(() =>
        setLoading(false)
      );
  }, [id, isDemo]);

  // ================= SAVE =================
  const handleSave = async () => {
    try {
      setSaving(true);

      const payload = {
        questype,
        description,
        quesbankid: bankId,
        quesJson: useJsonEditor
          ? JSON.parse(jsonText)
          : questions,
      };

      const updateUrl =
  `/pustak-questionbank/update/${id}?is_demo=${isDemo ? 1 : 0}`;

      await api.put(updateUrl, payload);

      setMessage(
        isDemo
          ? "✅ Demo updated successfully"
          : "✅ Live updated successfully"
      );

      setTimeout(
        () => navigate(-1),
        1200
      );
    } catch (err) {
      console.error(err);

      setMessage(
        "❌ Save failed / Invalid JSON"
      );
    } finally {
      setSaving(false);
    }
  };

  // ================= UPDATE HELPERS =================
  const updateMCQ = (
    i,
    key,
    value
  ) => {
    const arr = [...questions];

    arr[i][key] = value;

    setQuestions(arr);
  };

  const updateMCQOption = (
    qi,
    oi,
    value
  ) => {
    const arr = [...questions];

    arr[qi].options[oi] = value;

    setQuestions(arr);
  };

  // ================= RENDER EDITOR =================
  const renderEditor = () => {
    if (useJsonEditor) {
      return (
        <Card className="border-0 shadow-sm rounded-4">
          <Card.Body>
            <Form.Control
              as="textarea"
              rows={22}
              value={jsonText}
              onChange={(e) =>
                setJsonText(
                  e.target.value
                )
              }
              className="font-monospace bg-dark text-light"
              style={{
                fontSize: 14,
                borderRadius: 16,
              }}
            />
          </Card.Body>
        </Card>
      );
    }

    return (
      <Tabs
        defaultActiveKey={0}
        className="mb-3 custom-tabs"
      >
        {questions.map((q, index) => (
          <Tab
            key={index}
            eventKey={index}
            title={`Q${index + 1}`}
          >
            <Card className="mt-3 border-0 shadow-sm rounded-4">
              <Card.Body className="p-4">
                {/* QUESTION */}
                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">
                    Question
                  </Form.Label>

                  <Form.Control
                    size="lg"
                    value={
                      q.question || ""
                    }
                    onChange={(e) =>
                      updateMCQ(
                        index,
                        "question",
                        e.target.value
                      )
                    }
                    placeholder="Enter question"
                    className="rounded-4"
                  />
                </Form.Group>

                {/* MCQ */}
                {questype === "MCQ" && (
                  <>
                    <h6 className="fw-bold mb-3">
                      Options
                    </h6>

                    {q.options?.map(
                      (opt, oi) => (
                        <InputGroup
                          className="mb-3"
                          key={oi}
                        >
                          <InputGroup.Text>
                            {String.fromCharCode(
                              65 + oi
                            )}
                          </InputGroup.Text>

                          <Form.Control
                            value={opt}
                            onChange={(e) =>
                              updateMCQOption(
                                index,
                                oi,
                                e.target
                                  .value
                              )
                            }
                            placeholder={`Option ${String.fromCharCode(
                              65 + oi
                            )}`}
                            className="rounded-end"
                          />
                        </InputGroup>
                      )
                    )}
                  </>
                )}

                {/* TRUE FALSE */}
                {questype ===
                  "TRUE/FALSE" && (
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">
                      Select Answer
                    </Form.Label>

                    <Form.Select
                      size="lg"
                      value={
                        q.correct_answer
                      }
                      onChange={(e) =>
                        updateMCQ(
                          index,
                          "correct_answer",
                          e.target.value
                        )
                      }
                      className="rounded-4"
                    >
                      <option value="True">
                        True
                      </option>

                      <option value="False">
                        False
                      </option>
                    </Form.Select>
                  </Form.Group>
                )}

                {/* NOTES */}
                {questype === "NOTES" && (
                  <Form.Group>
                    <Form.Label className="fw-bold">
                      Notes Content
                    </Form.Label>

                    <Form.Control
                      as="textarea"
                      rows={8}
                      value={q.note}
                      onChange={(e) =>
                        updateMCQ(
                          index,
                          "note",
                          e.target.value
                        )
                      }
                      className="rounded-4"
                    />
                  </Form.Group>
                )}

                {/* ANSWER */}
                {questype !==
                  "NOTES" && (
                  <Card className="mt-4 border-0 bg-light rounded-4">
                    <Card.Body>
                      <Form.Label className="fw-bold">
                        Correct Answer
                      </Form.Label>

                      {questype ===
                      "MCQ" ? (
                        <Form.Select
                          size="lg"
                          value={
                            q.correct_answer
                          }
                          onChange={(e) =>
                            updateMCQ(
                              index,
                              "correct_answer",
                              e.target
                                .value
                            )
                          }
                          className="rounded-4"
                        >
                          {q.options?.map(
                            (
                              opt,
                              oi
                            ) => (
                              <option
                                key={
                                  oi
                                }
                                value={
                                  opt
                                }
                              >
                                {String.fromCharCode(
                                  65 +
                                    oi
                                )}
                                . {opt}
                              </option>
                            )
                          )}
                        </Form.Select>
                      ) : (
                        <Badge
                          bg="success"
                          className="fs-6 px-3 py-2"
                        >
                          {
                            q.correct_answer
                          }
                        </Badge>
                      )}
                    </Card.Body>
                  </Card>
                )}
              </Card.Body>
            </Card>
          </Tab>
        ))}
      </Tabs>
    );
  };

  // ================= LOADING =================
  if (loading) {
    return (
      <div className="container py-5 text-center">
        <Spinner
          animation="border"
          variant="primary"
        />

        <h5 className="mt-3">
          Loading Question Bank...
        </h5>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* ================= HEADER ================= */}
      <Card className="border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
        <div
          className="p-4 text-white"
          style={{
            background:
              "linear-gradient(135deg,#2563eb,#1e40af)",
          }}
        >
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h2 className="fw-bold mb-2">
                ✏️ Edit Question
                Bank
              </h2>

              <div className="d-flex gap-2 flex-wrap">
                <Badge bg="light" text="dark">
                  {questype}
                </Badge>

                {isDemo && (
                  <Badge bg="warning" text="dark">
                    DEMO MODE
                  </Badge>
                )}

                <Badge bg="dark">
                  {meta.seriesname ||
                    "Subject"}
                </Badge>

                <Badge bg="secondary">
                  {meta.bookname ||
                    "Chapter"}
                </Badge>
              </div>
            </div>

            <Button
              variant="light"
              onClick={() =>
                navigate(-1)
              }
            >
              ⬅ Back
            </Button>
          </div>
        </div>
      </Card>

      {/* ================= ALERT ================= */}
      {message && (
        <Alert
          variant={
            message.includes("✅")
              ? "success"
              : "danger"
          }
          className="rounded-4 shadow-sm"
        >
          {message}
        </Alert>
      )}

      {/* ================= META INFO ================= */}
      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <Card.Body>
          <Row>
            <Col md={3}>
              <div className="mb-3">
                <small className="text-muted">
                  Category
                </small>

                <h6 className="fw-bold">
                  {meta.catname ||
                    "-"}
                </h6>
              </div>
            </Col>

            <Col md={3}>
              <div className="mb-3">
                <small className="text-muted">
                  Class
                </small>

                <h6 className="fw-bold">
                  {meta.subcatname ||
                    "-"}
                </h6>
              </div>
            </Col>

            <Col md={3}>
              <div className="mb-3">
                <small className="text-muted">
                  Subject
                </small>

                <h6 className="fw-bold">
                  {meta.seriesname ||
                    "-"}
                </h6>
              </div>
            </Col>

            <Col md={3}>
              <div className="mb-3">
                <small className="text-muted">
                  Chapter
                </small>

                <h6 className="fw-bold">
                  {meta.bookname ||
                    "-"}
                </h6>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ================= SETTINGS ================= */}
      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <Card.Body>
          <Row className="g-4">
            {/* BANK */}
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">
                  Question Bank
                </Form.Label>

                <Form.Select
                  size="lg"
                  value={bankId}
                  onChange={(e) =>
                    setBankId(
                      e.target.value
                    )
                  }
                  className="rounded-4"
                >
                  <option value="">
                    Select Question Bank
                  </option>

                  {banks.map((b) => (
                    <option
                      key={b.bank_id}
                      value={b.bank_id}
                    >
                      {b.bank_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* TYPE */}
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">
                  Question Type
                </Form.Label>

                <Form.Select
                  size="lg"
                  value={questype}
                  onChange={(e) =>
                    setQuestype(
                      e.target.value
                    )
                  }
                  className="rounded-4"
                >
                  <option>
                    MCQ
                  </option>

                  <option>
                    TRUE/FALSE
                  </option>

                  <option>
                    NOTES
                  </option>

                  <option>
                    PPT
                  </option>

                  <option>
                    VIDEO
                  </option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* DESCRIPTION */}
          <Form.Group className="mt-4">
            <Form.Label className="fw-bold">
              AI Prompt / Description
            </Form.Label>

            <Form.Control
              as="textarea"
              rows={4}
              value={description}
              onChange={(e) =>
                setDescription(
                  e.target.value
                )
              }
              placeholder="Enter description..."
              className="rounded-4"
            />
          </Form.Group>

          {/* JSON TOGGLE */}
          <div className="mt-4">
            <Form.Check
              type="switch"
              label="Advanced JSON Editor"
              className="fw-bold"
              checked={useJsonEditor}
              onChange={() =>
                setUseJsonEditor(
                  !useJsonEditor
                )
              }
            />
          </div>
        </Card.Body>
      </Card>

      {/* ================= CONTENT ================= */}
      {renderEditor()}

      {/* ================= FOOTER ================= */}
      <Card className="border-0 shadow-sm rounded-4 mt-4">
        <Card.Body>
          {saving && (
            <div className="mb-3">
              <ProgressBar
                animated
                now={100}
              />
            </div>
          )}

          <div className="d-flex gap-3 flex-wrap">
            <Button
              variant="primary"
              size="lg"
              disabled={saving}
              onClick={handleSave}
              className="px-5 rounded-pill"
            >
              {saving ? (
                <>
                  <Spinner
                    size="sm"
                    animation="border"
                    className="me-2"
                  />
                  Saving...
                </>
              ) : (
                "💾 Save Changes"
              )}
            </Button>

            <Button
              variant="outline-secondary"
              size="lg"
              className="rounded-pill"
              onClick={() =>
                navigate(-1)
              }
            >
              Cancel
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}